/**
 * PriceHistoryDialog.jsx — read-only audit trail of unit_price mutations
 * for a single SKU. Lazy-loads the history when opened so we don't pay the
 * round-trip on rows the user never inspects.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { ArrowUp, ArrowDown, ClockClockwise } from '@phosphor-icons/react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { getSkuPriceHistory } from '../lib/api';

const MODE_LABEL = {
  manual: 'Manual edit',
  bulk_set: 'Bulk · set price',
  bulk_percent_delta: 'Bulk · percent',
  bulk_absolute_delta: 'Bulk · amount',
};

export default function PriceHistoryDialog({ sku, open, onOpenChange }) {
  const [rows, setRows] = useState(null); // null = loading, [] = empty
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !sku?.id) return;
    let cancelled = false;
    setRows(null); setError(null);
    getSkuPriceHistory(sku.id)
      .then((r) => { if (!cancelled) setRows(r.data || []); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load history'); });
    return () => { cancelled = true; };
  }, [open, sku?.id]);

  // Build the price-over-time series for the sparkline. The API returns
  // newest-first; reverse, prepend the oldest "before" as the starting
  // datapoint, and append the current SKU price as the most recent point if
  // it differs from the latest logged "after" (handles the case where the
  // user reset to the same value via no-op or where the data is stale).
  const chartData = useMemo(() => {
    if (!rows || rows.length === 0) return [];
    const ordered = [...rows].reverse(); // oldest first
    const points = [
      { ts: ordered[0].changed_at, price: ordered[0].before, label: 'Initial' },
      ...ordered.map((r) => ({ ts: r.changed_at, price: r.after, label: r.mode })),
    ];
    return points;
  }, [rows]);

  // Compute summary stats for the chart header
  const stats = useMemo(() => {
    if (!chartData.length) return null;
    const prices = chartData.map((p) => p.price);
    const first = prices[0];
    const last = prices[prices.length - 1];
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const totalChange = last - first;
    const totalPct = first > 0 ? (totalChange / first) * 100 : 0;
    return { first, last, min, max, totalChange, totalPct };
  }, [chartData]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="price-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClockClockwise size={18} weight="fill" />
            Price history — <span className="font-mono text-brand">{sku?.sku_code}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="mt-2">
          {rows === null && !error && (
            <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>
          )}
          {error && (
            <div className="text-sm text-rose-600 py-4">{error}</div>
          )}
          {rows !== null && rows.length === 0 && !error && (
            <div className="text-sm text-muted-foreground py-8 text-center" data-testid="price-history-empty">
              No price changes logged yet — every future edit will appear here.
            </div>
          )}
          {rows && rows.length > 0 && (
            <>
              {/* Trend chart — quick "where has this SKU been priced over time" view */}
              <div className="border border-border rounded-md p-3 mb-3 bg-card" data-testid="price-history-chart">
                <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
                  <div className="flex items-baseline gap-3 text-xs">
                    <span className="text-muted-foreground uppercase tracking-wider font-bold">Trend</span>
                    {stats && (
                      <>
                        <span className="text-muted-foreground">First <span className="font-mono font-bold text-foreground">${stats.first.toFixed(2)}</span></span>
                        <span className="text-muted-foreground">Now <span className="font-mono font-bold text-foreground">${stats.last.toFixed(2)}</span></span>
                        <span className={`flex items-center gap-1 font-mono font-bold ${stats.totalChange >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                          {stats.totalChange >= 0 ? <ArrowUp size={11} weight="bold" /> : <ArrowDown size={11} weight="bold" />}
                          {stats.totalChange >= 0 ? '+' : ''}${Math.abs(stats.totalChange).toFixed(2)}
                          <span className="text-[10px] opacity-80">({stats.totalPct >= 0 ? '+' : ''}{stats.totalPct.toFixed(1)}%)</span>
                        </span>
                      </>
                    )}
                  </div>
                  {stats && (
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      Range <span className="font-mono text-foreground">${stats.min.toFixed(2)} – ${stats.max.toFixed(2)}</span>
                    </div>
                  )}
                </div>
                <div style={{ width: '100%', height: 140 }}>
                  <ResponsiveContainer>
                    <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,.08)" />
                      <XAxis
                        dataKey="ts"
                        tickFormatter={(v) => v ? new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}
                        tick={{ fontSize: 10, fill: 'currentColor', opacity: 0.7 }}
                        stroke="rgba(0,0,0,.2)"
                        minTickGap={28}
                      />
                      <YAxis
                        tickFormatter={(v) => `$${v.toFixed(2)}`}
                        tick={{ fontSize: 10, fill: 'currentColor', opacity: 0.7 }}
                        stroke="rgba(0,0,0,.2)"
                        domain={['auto', 'auto']}
                        width={56}
                      />
                      <Tooltip
                        formatter={(v) => [`$${Number(v).toFixed(2)}`, 'Price']}
                        labelFormatter={(v) => v ? new Date(v).toLocaleString() : ''}
                        contentStyle={{ fontSize: 12, padding: '6px 10px' }}
                      />
                      <Line
                        type="monotone"
                        dataKey="price"
                        stroke="hsl(var(--brand, 220 70% 50%))"
                        strokeWidth={2}
                        dot={{ r: 3, fill: 'hsl(var(--brand, 220 70% 50%))' }}
                        activeDot={{ r: 5 }}
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="border border-border rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-2">When</th>
                    <th className="text-left px-3 py-2">Before</th>
                    <th className="text-left px-3 py-2">After</th>
                    <th className="text-left px-3 py-2">Δ</th>
                    <th className="text-left px-3 py-2">Mode</th>
                    <th className="text-left px-3 py-2">By</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const delta = (r.after ?? 0) - (r.before ?? 0);
                    const up = delta > 0;
                    return (
                      <tr key={i} className="border-t border-border" data-testid={`price-history-row-${i}`}>
                        <td className="px-3 py-2 text-muted-foreground text-xs whitespace-nowrap">
                          {r.changed_at ? new Date(r.changed_at).toLocaleString() : '—'}
                        </td>
                        <td className="px-3 py-2 font-mono">${(r.before ?? 0).toFixed(2)}</td>
                        <td className="px-3 py-2 font-mono font-bold">${(r.after ?? 0).toFixed(2)}</td>
                        <td className={`px-3 py-2 font-mono text-xs flex items-center gap-1 ${up ? 'text-emerald-700' : 'text-rose-700'}`}>
                          {up ? <ArrowUp size={12} weight="bold" /> : <ArrowDown size={12} weight="bold" />}
                          {up ? '+' : ''}{delta.toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-xs uppercase tracking-wider">
                          <span className={`px-1.5 py-0.5 rounded ${r.mode === 'manual' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>
                            {MODE_LABEL[r.mode] || r.mode}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[160px]" title={r.changed_by_email}>
                          {r.changed_by_email || '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
